import React from "react";
//#region types
import { FieldArrayRenderProps, FormikProps, useFormikContext } from "formik";
import { StyleEnums } from "models/enums";
import { EditableTableMoreActionMenuItemType } from "models/propTypes/genericFormProps";
import { TableRowCommonKeys, TableRowProperties } from "models/propTypes/simpleTableProps";
import { TranslationKeys } from "translations";
import { IconDefinitionConstants } from "utils/constants";
//#endregion
//#region components
import { Chip } from "@mui/material";
import MenuButton from "../../buttons/menuButton";
import RoundButton from "../../buttons/roundButton";
import Icon from "../../icon";
//#endregion
import cssExports from "theme/styles/main.module.scss";
import { LocalizationHelperInstance } from "utils/helpers";

function useTableListFormFieldHelpers<T>(
  value: any[],
  resultTableProperties: Partial<Record<keyof (T & TableRowCommonKeys), TableRowProperties>>,
  idField?: keyof T,
  moreActions?: (row: any, formikProps?: FormikProps<any>) => EditableTableMoreActionMenuItemType[],
  defaultRefField?: keyof T,
  isReadOnly?: boolean,
  hideMoreActionsInForm?: boolean,
  onRemoveItem?: (row: any) => Promise<any>
) {
  const onClickRemove = async (index: number, item: any, arrayHelpers: FieldArrayRenderProps) => {
    if (onRemoveItem) {
      await onRemoveItem(item).then((res) => {
        removeItemFromList(index, item, arrayHelpers);
      });
    } else {
      removeItemFromList(index, item, arrayHelpers);
    }
  };

  const removeItemFromList = async (index: number, item: any, arrayHelpers: FieldArrayRenderProps) => {
    const id = idField ? item[idField] : item.id;
    // eslint-disable-next-line eqeqeq --- disabling to avoid regression testing if changed
    const itemIndex = id && (value as any[]).findIndex((v: any) => (idField ? v[idField] == id : v.id == id));
    arrayHelpers.remove(itemIndex);
  };

  const formikProps = useFormikContext();

  const getMoreActions = (index: number, row: any, arrayHelpers: FieldArrayRenderProps) =>
    moreActions
      ? moreActions(row, formikProps).map((m) =>
          typeof m === "object" ? { ...m, onClickHandler: m.onClickHandler?.bind(null, index, row, formikProps) } : m
        )
      : [
          {
            description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Delete),
            icon: IconDefinitionConstants.WTrashAlt,
            highlightType: StyleEnums.DisplayType.Danger,
            onClickHandler: onClickRemove.bind(null, index, row, arrayHelpers),
          },
        ];

  const getFormMoreActions = (index: number, row: any, arrayHelpers: FieldArrayRenderProps) => {
    if (hideMoreActionsInForm) return [];
    getMoreActions(index, row, arrayHelpers);
  };

  const getMoreActionsButton = (index: number, row: any, arrayHelpers: FieldArrayRenderProps) => (
    <MenuButton
      menuPlacement="bottom"
      popperOffSet={[16, 2]}
      menuItems={getMoreActions(index, row, arrayHelpers)}
      button={
        <RoundButton className={cssExports.tableListMoreActions}>
          <Icon icon={IconDefinitionConstants.WEllipsisH} />
        </RoundButton>
      }
      classes={{
        menuOpen: cssExports.tableListMoreActionsMenuOpen,
      }}
    />
  );

  const getProperties = (arrayHelpers: FieldArrayRenderProps) => {
    const newProperties = defaultRefField
      ? {
          ...resultTableProperties,
          [defaultRefField as string]: {
            formatter: (row: any) =>
              row[defaultRefField] ? (
                <Chip
                  label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Default)}
                  className={cssExports.greenTag}
                />
              ) : (
                ""
              ),
            alignment: "right",
          },
        }
      : { ...resultTableProperties };
    return {
      ...newProperties,
      select: !isReadOnly
        ? {
            formatter: (row: any, rowIndex: number) => getMoreActionsButton(rowIndex, row, arrayHelpers),
            width: 24,
            className: cssExports.moreActions,
          }
        : undefined,
    };
  };

  return { getProperties, getFormMoreActions };
}

export default useTableListFormFieldHelpers;
